import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Send, Loader2, AlertCircle, Shield, Clock } from "lucide-react";

interface SelectedPlan {
  id?: string | number;
  name: string;
  coverageType: string;
  carrier?: string;
  carrierName?: string;
  premium: number;
  employerPays: number;
  employeePays: number;
}

interface QuoteAcceptanceFormProps {
  token: string;
  companyName: string;
  isVersioned?: boolean;
  acceptance?: any;
  onAccepted?: () => void;
  selectedPlans?: SelectedPlan[];
  disabled?: boolean;
  /** True when a logged-in client (or the owning broker) is viewing their own
   * quote — enables a session-verified, finalizing acceptance. Otherwise the
   * submission is stored as pending for the broker to confirm. */
  verified?: boolean;
  /** Quote id — required for the verified (authenticated) accept endpoint. */
  quoteId?: number;
  /** The broker has shared a newer version since this link was sent. */
  superseded?: boolean;
  /** The split the client set on the Contribution Calculator. Recorded as part
   * of the acceptance: it's what they agreed to pay, and until now it only ever
   * existed in their browser as an input to the displayed totals. */
  contributionStrategy?: {
    mode: "percent" | "flat";
    /** Percent of the employee tier the EMPLOYER pays. */
    employerPercentEmployeeTier: number;
    /** Percent of dependant premium the EMPLOYER pays. */
    employerPercentDependents: number;
    employerFlatEmployeeTier?: number;
    employerFlatDependents?: number;
  };
}

export function QuoteAcceptanceForm({
  token,
  companyName,
  isVersioned = false,
  acceptance,
  onAccepted,
  selectedPlans = [],
  disabled = false,
  verified = false,
  quoteId,
  contributionStrategy,
  superseded,
}: QuoteAcceptanceFormProps) {
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [comments, setComments] = useState("");
  const [acknowledgements, setAcknowledgements] = useState({
    reviewedPlans: false,
    understandTerms: false,
    authorizedSigner: false,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      // Verified (logged-in) client → authenticated finalizing endpoint.
      // Otherwise → public endpoint, which stores selections as pending.
      const endpoint = verified && quoteId
        ? `/api/portal/quote/${quoteId}/accept`
        : isVersioned
        ? `/api/public/quote-version/${token}/accept`
        : `/api/public/quote/${token}/accept`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          signerName,
          signerTitle,
          signerEmail,
          comments,
          acknowledgementsJson: acknowledgements,
          strategyJson: contributionStrategy ?? null,
          selectedPlans: selectedPlans.map(p => ({
            id: p.id,
            name: p.name,
            coverageType: p.coverageType,
            carrier: p.carrier || p.carrierName,
            premium: p.premium,
            employerPays: p.employerPays,
            employeePays: p.employeePays,
          })),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to accept quote");
      }

      return response.json();
    },
    onSuccess: () => {
      onAccepted?.();
    },
  });

  const allAcknowledged = Object.values(acknowledgements).every(Boolean);
  const isValid = signerName && signerEmail && allAcknowledged && !disabled;

  if (acceptance?.status === "accepted") {
    return (
      <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
        <CardContent className="py-8 text-center">
          <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-600" />
          <h3 className="text-xl font-semibold text-green-800 dark:text-green-200 mb-2">
            Quote Accepted
          </h3>
          <p className="text-green-700 dark:text-green-300 mb-4">
            This quote was accepted by {acceptance.signerName}
            {acceptance.acceptedAt && (
              <> on {new Date(acceptance.acceptedAt).toLocaleDateString()}</>
            )}
          </p>
          <p className="text-sm text-green-600 dark:text-green-400">
            Your benefits administrator will be in touch with next steps.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Readable, but not acceptable: agreeing to a withdrawn version would record
  // the client signing something the broker has already replaced. Checked after
  // "accepted", since a done deal outranks "there is a newer version".
  if (superseded) {
    return (
      <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40">
        <CardContent className="py-8 text-center">
          <Clock className="h-12 w-12 mx-auto mb-4 text-amber-600 dark:text-amber-500" />
          <h3 className="text-xl font-semibold text-amber-900 dark:text-amber-200 mb-2">
            This quote has been updated
          </h3>
          <p className="text-amber-800 dark:text-amber-300">
            What you see above is the version you were originally sent. Your broker has since sent a
            newer one, so please use their most recent link to accept.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (acceptance?.status === "pending") {
    const submittedAt = acceptance.updatedAt ?? acceptance.createdAt;
    return (
      <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
        <CardContent className="py-8 text-center">
          <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-blue-600" />
          <h3 className="text-xl font-semibold text-blue-800 dark:text-blue-200 mb-2">
            Selections sent
          </h3>
          <p className="text-blue-700 dark:text-blue-300 mb-4">
            Submitted by {acceptance.signerName}
            {submittedAt && <> on {new Date(submittedAt).toLocaleDateString()}</>}.
          </p>
          <p className="text-sm text-blue-600 dark:text-blue-400">
            Your broker is reviewing them and will confirm. Nothing else is needed from you. If
            something looks wrong, contact them and they can reopen it.
          </p>
        </CardContent>
      </Card>
    );
  }


  if (acceptMutation.isSuccess) {
    const pending = (acceptMutation.data as any)?.pending;
    return (
      <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
        <CardContent className="py-8 text-center">
          <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-600" />
          <h3 className="text-xl font-semibold text-green-800 dark:text-green-200 mb-2">
            {pending ? "Selections Sent" : "Thank You!"}
          </h3>
          <p className="text-green-700 dark:text-green-300 mb-4">
            {pending
              ? "Your selections were sent to your broker for confirmation. They'll be in touch with next steps."
              : "Your acceptance has been recorded. Your benefits administrator will be in touch with next steps."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={disabled ? "opacity-60" : ""}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          {verified ? "Accept This Quote" : "Submit Your Selections"}
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          {disabled
            ? "Please select at least one plan above before completing the form."
            : verified
            ? "Please review the benefits proposal above and complete the form below to accept."
            : "Review the proposal above and submit your selections. Your broker will confirm and finalize the acceptance."}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {disabled && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You must select at least one plan from the quote to proceed with acceptance.
            </AlertDescription>
          </Alert>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="signerName">Your Full Name *</Label>
            <Input
              id="signerName"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Enter your full name"
              disabled={disabled}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="signerTitle">Your Title</Label>
            <Input
              id="signerTitle"
              value={signerTitle}
              onChange={(e) => setSignerTitle(e.target.value)}
              placeholder="e.g., HR Manager, CEO"
              disabled={disabled}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="signerEmail">Your Email Address *</Label>
          <Input
            id="signerEmail"
            type="email"
            value={signerEmail}
            onChange={(e) => setSignerEmail(e.target.value)}
            placeholder="you@company.com"
            disabled={disabled}
          />
        </div>

        <div className="space-y-4">
          <Label>Acknowledgements *</Label>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox
                id="ack-reviewed"
                checked={acknowledgements.reviewedPlans}
                onCheckedChange={(checked) =>
                  setAcknowledgements({ ...acknowledgements, reviewedPlans: !!checked })
                }
                disabled={disabled}
              />
              <label htmlFor="ack-reviewed" className="text-sm leading-tight cursor-pointer">
                I have reviewed the benefit plans, coverage details, and premium information presented in this proposal.
              </label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="ack-terms"
                checked={acknowledgements.understandTerms}
                onCheckedChange={(checked) =>
                  setAcknowledgements({ ...acknowledgements, understandTerms: !!checked })
                }
                disabled={disabled}
              />
              <label htmlFor="ack-terms" className="text-sm leading-tight cursor-pointer">
                I understand that this acceptance indicates our intent to enroll in the selected benefits program.
              </label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="ack-authorized"
                checked={acknowledgements.authorizedSigner}
                onCheckedChange={(checked) =>
                  setAcknowledgements({ ...acknowledgements, authorizedSigner: !!checked })
                }
                disabled={disabled}
              />
              <label htmlFor="ack-authorized" className="text-sm leading-tight cursor-pointer">
                I am authorized to make benefits decisions on behalf of {companyName}.
              </label>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="comments">Additional Comments (Optional)</Label>
          <Textarea
            id="comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Any questions or special requests..."
            rows={3}
            disabled={disabled}
          />
        </div>

        {acceptMutation.isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {acceptMutation.error instanceof Error
                ? acceptMutation.error.message
                : "Failed to accept quote. Please try again."}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter>
        <Button
          className="w-full"
          size="lg"
          disabled={!isValid || acceptMutation.isPending}
          onClick={() => acceptMutation.mutate()}
        >
          {acceptMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              {verified ? "Accept Quote" : "Submit Selections"}
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
